import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding,  } from '../../context/OnboardingContext';
import {
  LuCompass, LuLayoutGrid, LuFolder, LuDownload, LuEye,
  LuSettings, LuCode, LuPlay, LuCheck, LuCircleDot, LuPuzzle, LuSearch,
  LuCpu, LuWifi, LuHardDrive, LuTestTube, LuShieldCheck,
  LuGitBranch, LuRocket, LuTrophy, LuRefreshCw, LuFileCheck, LuSparkles, LuVideo, LuRadar
} from 'react-icons/lu';
import '../../styles/wizard.css';
import '../../styles/wizard-rt3.css';
import { INTENT_TYPES } from '../../Config/Onboarding/constant';
import { getCurrentStepFlow, IOT_HARDWARE_STEPS } from '../../Config/Onboarding/stepFlow';

// Icon mapping for different step types
const stepIconMap = {
  'intent_selection': LuCompass,
  'workspace_setup': LuLayoutGrid,
  'project_setup': LuFolder,
  'test_source': LuDownload,
  'test_creation_mode': LuPuzzle,
  'preview': LuEye,
  'ai_preview': LuSparkles,
  'generate': LuCode,
  'setup_tools': LuSettings,
  'recording_url': LuVideo,
  'review_recording': LuEye,
  'discovery_setup': LuRadar,
  'discovery_crawl': LuRadar,
  'review_discovery': LuEye,
  'run': LuPlay,
  'complete': LuCheck,
  'conversion_settings': LuSettings,
  'convert': LuRefreshCw,
  'jira_config': LuFileCheck,
  'jira_selection': LuSearch,
  'interaction_mode': LuWifi,
  'lab_agent_setup': LuCpu,
  'device_setup': LuHardDrive,
  'setup': LuSettings,
  'create_test': LuTestTube,
  'validation': LuShieldCheck,
};

const SHORT_LABELS = {
  'Select Intent': 'Intent',
  'Create Workspace': 'Workspace',
  'Setup Project': 'Project',
  'Project Setup': 'Project',
  'Import Tests': 'Requirements',
  'Test Creation Mode': 'Create tests',
  'Generate Scripts': 'Generate',
  'Generate Tests': 'Generate',
  'Execute Test': 'Execute',
  'Run Test': 'Execute',
};

const shortLabel = (label = '') => SHORT_LABELS[label] || label;

const Stepper = ({ currentStep, completedSteps = [], className = '', onStepClick, mode }) => {
  const navigate = useNavigate();
  const onboardingContext = useOnboarding();
  
  // Get steps from engine
  const { engine, data } = onboardingContext;
  const { flow } = engine;
  const { testCreationMode ,project} = data;
  const applicationType = project?.applicationType;
  


  // Build stepsList from flow.stepOrder using dynamic step definitions
// Stepper.js — replace the stepsList building block

let stepsList = [];
if (flow.stepOrder && flow.stepOrder.length > 0) {
  let stepDefinitions;

  if (applicationType === 'iot_hardware') {
    const iotStepMap = {};
    IOT_HARDWARE_STEPS.forEach(s => { iotStepMap[s.id] = s; });
    stepDefinitions = flow.stepOrder.map(id =>
      iotStepMap[id] || { id, label: `Step ${id}`, key: `step_${id}` }
    );
  }  else {
  // For all non-IoT types, use intent + testCreationMode aware flow
  // ✅ Fall back to MANUAL_TO_AUTOMATION when intent is null (app type selected before intent)
  const resolvedIntent = engine.intent || INTENT_TYPES.MANUAL_TO_AUTOMATION;
  const modeAwareFlow = getCurrentStepFlow(resolvedIntent, testCreationMode);
  // Use mode flow as source of truth so removed steps (e.g. Live Recording) never appear
  stepDefinitions = modeAwareFlow;
}

  stepsList = stepDefinitions.map(step => ({
    number: step.id,
    label: step.label,
    key: step.key,
    Icon: stepIconMap[step.key] || LuCircleDot
  }));
}

  // Prefer engine current step so completed/in-progress stay in sync with navigation
  const activeStep = flow.currentStep || currentStep;

  // Check if step 4 is completed and restrict navigation for certain conditions
  const isStep4Completed = flow.completedSteps.includes(4);
  const isGenerateTestsIntent = engine.intent === INTENT_TYPES.JIRA_REQUIREMENTS;
  const shouldRestrictNavigation = isStep4Completed && isGenerateTestsIntent;

  const getCurrentStepIndex = () => {
    if (typeof activeStep === 'number') {
      return activeStep - 1;
    }
    return 0;
  };

  const isStepCompleted = (stepNumber) => {
    // Current in-progress step should not render as completed
    if (stepNumber === activeStep) return false;
    return flow.completedSteps.includes(stepNumber) || completedSteps.includes(stepNumber);
  };

  const isStepActive = (stepNumber) => {
    return activeStep === stepNumber;
  };

  const isStepClickable = (stepNumber) => {
    // If step 4 is completed and conditions met, only allow navigation to steps >= currentStep
    if (shouldRestrictNavigation && stepNumber < activeStep) {
      return false;
    }
    return isStepCompleted(stepNumber) || isStepActive(stepNumber);
  };

  const handleStepClick = (stepNumber) => {
    if (isStepClickable(stepNumber) && stepNumber !== activeStep) {
      if (onStepClick) {
        onStepClick(stepNumber);
      } else {
        navigate(`/app/onboarding?step=${stepNumber}`);
      }
    }
  };

  const currentStepIndex = getCurrentStepIndex();

  const trackRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const updateEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const active = el.querySelector('[aria-current="step"]');
    if (active) {
      const target = active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2;
      el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [activeStep, stepsList.length, updateEdges]);

  if (!stepsList || stepsList.length === 0) {
    return null; // Don't render if no steps yet
  }

  const total = stepsList.length;
  const foundIndex = stepsList.findIndex((s) => s.number === activeStep);
  const position = foundIndex >= 0 ? foundIndex : Math.min(Math.max(currentStepIndex, 0), total - 1);
  const currentLabel = stepsList[position]?.label || '';
  const completedCount = stepsList.filter((s) => isStepCompleted(s.number)).length;
  const percent = Math.round((completedCount / total) * 100);
  const showClickable = typeof onStepClick === 'function';

  return (
    <nav className={`ob-stepper ${className}`} aria-label="Setup progress">
      <div className="ob-stepper__summary">
        <span className="ob-stepper__count">
          Step {position + 1} of {total} · <strong>{currentLabel}</strong>
        </span>
        <span className="ob-stepper__percent">{percent}% complete</span>
      </div>
      <div
        className="ob-stepper__bar"
        role="progressbar"
        aria-label="Setup completion"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div className="ob-stepper__bar-fill" style={{ width: `${Math.max(percent, 2)}%` }} />
      </div>
      <div className={`ob-stepper__viewport ${edges.left ? 'has-left' : ''} ${edges.right ? 'has-right' : ''}`}>
        <ol className="ob-stepper__track" ref={trackRef} onScroll={updateEdges}>
          {stepsList.map((step, index) => {
            const isCompleted = isStepCompleted(step.number);
            const isActive = isStepActive(step.number);
            const isClickable = isStepClickable(step.number) && !isActive;
            const interactive = showClickable && isClickable;
            const state = isActive ? 'current' : isCompleted ? 'complete' : 'upcoming';
            const statusText = isActive ? 'current step' : isCompleted ? 'completed' : 'not started';

            return (
              <li key={step.key} className="ob-stepper__item">
                <div
                  className={`ob-step is-${state} ${interactive ? 'is-clickable' : ''}`}
                  aria-current={isActive ? 'step' : undefined}
                  title={step.label}
                  onClick={() => handleStepClick(step.number)}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  aria-label={`Step ${index + 1}: ${step.label}, ${statusText}`}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleStepClick(step.number);
                    }
                  }}
                >
                  <span className="ob-step__dot" aria-hidden="true">
                    {isCompleted ? <LuCheck size={13} strokeWidth={3} /> : index + 1}
                  </span>
                  <span className="ob-step__label">{shortLabel(step.label)}</span>
                </div>
                {index < stepsList.length - 1 && (
                  <span className={`ob-stepper__sep ${isCompleted ? 'is-complete' : ''}`} aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

// Compact version for mobile
export const CompactStepper = ({ currentStep, totalSteps = 9 }) => {
  const percentage = (currentStep / totalSteps) * 100;

  return (
    <div className="wizard-stepper-compact">
      <div className="stepper-compact-header">
        <span className="stepper-compact-text">Step {currentStep} of {totalSteps}</span>
        <span className="stepper-compact-percentage">{Math.round(percentage)}%</span>
      </div>
      <div className="stepper-compact-bar">
        <div 
          className="stepper-compact-fill" 
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default Stepper;